import { supabase } from '../lib/supabase';

/** מזהה המשתמש מהסשן המקומי — כל שליפה מסוננת בו במפורש ולא נשענת על RLS בלבד
 *  (למלווה יש SELECT גם על הרישומים והשקילות של מי ששיתף איתו). */
async function currentUserId(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('no_session');
  return session.user.id;
}

// ---------- טיפוסים ----------

export interface WeightPoint {
  date: string; // YYYY-MM-DD מקומי
  weightKg: number;
}

export interface DayCalories {
  date: string; // YYYY-MM-DD מקומי
  calories: number; // סכום היום (0 אם לא תועד)
  logged: boolean; // האם היו רישומים ביום זה
}

export interface WeeklyStats {
  avgCalories: number | null; // ממוצע קק"ל יומי — רק על ימים מתועדים; null אם אין
  loggedDays: number; // 0..7
  weightChangeKg: number | null; // ממוצע-מגמה שבוע אחרון פחות שבוע קודם; null באין די נתונים
}

// ---------- עזרי תאריך מקומי (לא UTC — כמו entries.ts) ----------

/** YYYY-MM-DD מקומי של תאריך נתון */
function localDateStr(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** תאריך מקומי לפני `back` ימים */
function localDayBack(back: number): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() - back);
}

/** כמו localDayBack, אבל יחסית לעוגן נתון (לא תמיד "עכשיו") — לדפדוף אחורה/קדימה בדוח. */
function dayBackFrom(anchor: Date, back: number): Date {
  return new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() - back);
}

/** גבולות יום מקומי כ-timestamptz לשאילתה — אותה גישה כמו localDayRange ב-entries.ts */
function localDayStart(date: Date): string {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).toISOString();
}

// ---------- משקל ----------

/** שקילות המשתמש מ-`days` הימים האחרונים, ממוין מהישן לחדש */
export async function fetchWeights(days = 42): Promise<WeightPoint[]> {
  const userId = await currentUserId();
  const { data, error } = await supabase
    .from('litebite_weights')
    .select('date, weight_kg')
    .eq('user_id', userId)
    .gte('date', localDateStr(localDayBack(days - 1)))
    .order('date', { ascending: true });
  if (error) throw error;
  const rows = (data ?? []) as { date: string; weight_kg: number }[];
  return rows.map((r) => ({ date: r.date, weightKg: Number(r.weight_kg) }));
}

/** כמו fetchWeights, אבל עם קצה עליון מפורש (לא תמיד "עכשיו") — לדפדוף בדוח לתקופות עבר,
 *  כדי שלא ידלוף מידע מאוחר יותר לתוך חישוב שינוי-משקל של תקופה היסטורית. */
async function fetchWeightsUntil(anchorEnd: Date, days: number): Promise<WeightPoint[]> {
  const from = localDateStr(new Date(anchorEnd.getFullYear(), anchorEnd.getMonth(), anchorEnd.getDate() - days + 1));
  const to = localDateStr(anchorEnd);
  const userId = await currentUserId();
  const { data, error } = await supabase
    .from('litebite_weights')
    .select('date, weight_kg')
    .eq('user_id', userId)
    .gte('date', from)
    .lte('date', to)
    .order('date', { ascending: true });
  if (error) throw error;
  const rows = (data ?? []) as { date: string; weight_kg: number }[];
  return rows.map((r) => ({ date: r.date, weightKg: Number(r.weight_kg) }));
}

/**
 * ממוצע נע נגרר (trailing): כל נקודה = ממוצע עד `window` השקילות האחרונות עד אליה, כולל.
 * נבחר נגרר ולא מרכזי — קצה הקו משקף את המשקל המוחלק הנוכחי בלי להסתמך על נתוני עתיד.
 */
export function movingAverage(points: WeightPoint[], window = 7): WeightPoint[] {
  return points.map((p, i) => {
    const slice = points.slice(Math.max(0, i - window + 1), i + 1);
    const sum = slice.reduce((s, q) => s + q.weightKg, 0);
    return { date: p.date, weightKg: sum / slice.length };
  });
}

// ---------- קלוריות שבועיות ----------

/** סכום קלוריות לכל אחד מ-7 הימים האחרונים (כולל היום) לפי יום מקומי — מהישן לחדש */
export async function fetchWeekCalories(): Promise<DayCalories[]> {
  const from = localDayStart(localDayBack(6));
  const to = localDayStart(localDayBack(-1)); // תחילת מחר = סוף היום
  const userId = await currentUserId();
  const { data, error } = await supabase
    .from('litebite_entries')
    .select('eaten_at, calories')
    .eq('user_id', userId)
    .gte('eaten_at', from)
    .lt('eaten_at', to);
  if (error) throw error;

  // קיבוץ לפי היום המקומי של כל רישום
  const byDay = new Map<string, number>();
  for (const r of (data ?? []) as { eaten_at: string; calories: number | null }[]) {
    const key = localDateStr(new Date(r.eaten_at));
    byDay.set(key, (byDay.get(key) ?? 0) + (r.calories ?? 0));
  }

  const days: DayCalories[] = [];
  for (let back = 6; back >= 0; back--) {
    const key = localDateStr(localDayBack(back));
    const sum = byDay.get(key);
    days.push({ date: key, calories: sum ?? 0, logged: sum !== undefined });
  }
  return days;
}

// ---------- סיכום שבועי ----------

/** חישוב טהור — מאפשר למסך לחשב בלי שליפות כפולות (weights: לפחות ~21 יום אחורה) */
export function computeWeeklyStats(week: DayCalories[], weights: WeightPoint[]): WeeklyStats {
  const logged = week.filter((d) => d.logged);
  const avgCalories = logged.length
    ? logged.reduce((s, d) => s + d.calories, 0) / logged.length
    : null;

  // שינוי משקל: ממוצע קו-המגמה ב-7 הימים האחרונים מול 7 הימים שלפניהם
  const trend = movingAverage(weights);
  const lastFrom = localDateStr(localDayBack(6));
  const prevFrom = localDateStr(localDayBack(13));
  const last = trend.filter((p) => p.date >= lastFrom);
  const prev = trend.filter((p) => p.date >= prevFrom && p.date < lastFrom);
  const mean = (arr: WeightPoint[]) => arr.reduce((s, p) => s + p.weightKg, 0) / arr.length;
  const weightChangeKg = last.length && prev.length ? mean(last) - mean(prev) : null;

  return { avgCalories, loggedDays: logged.length, weightChangeKg };
}

export async function fetchWeeklyStats(): Promise<WeeklyStats> {
  const [week, weights] = await Promise.all([fetchWeekCalories(), fetchWeights(28)]);
  return computeWeeklyStats(week, weights);
}

/**
 * קצב שינוי משקל "רחב" (לא שבוע-מול-שבוע שיכול להיות רועש) — שיפוע רגרסיה לינארית
 * על כל קו-המגמה הזמין (עד 6 שבועות). יציב יותר להערכת תאריך הגעה ליעד.
 * מחזיר ק"ג/שבוע; חיובי = ירידה. null אם אין מספיק נתונים (פחות מ-4 נקודות מגמה).
 */
export function estimateWeeklyPace(trend: WeightPoint[]): number | null {
  if (trend.length < 4) return null;
  const t0 = new Date(trend[0].date).getTime();
  const xs = trend.map((p) => (new Date(p.date).getTime() - t0) / 86400000); // ימים מהנקודה הראשונה
  const ys = trend.map((p) => p.weightKg);
  const n = xs.length;
  const sumX = xs.reduce((a, b) => a + b, 0);
  const sumY = ys.reduce((a, b) => a + b, 0);
  const sumXY = xs.reduce((s, x, i) => s + x * ys[i], 0);
  const sumXX = xs.reduce((s, x) => s + x * x, 0);
  const denom = n * sumXX - sumX * sumX;
  if (denom === 0) return null; // כל הנקודות באותו יום (לא אמור לקרות בפועל)
  const slopePerDay = (n * sumXY - sumX * sumY) / denom; // ק"ג/יום; שלילי = ירידה
  return -slopePerDay * 7; // הופך לסימן "חיובי=ירידה" (כמו יתר הפונקציות כאן), ק"ג/שבוע
}

// ---------- דוח תקופתי (שבועי/חודשי) ----------

export interface PeriodReport {
  days: number; // 7 / 30
  loggedDays: number;
  avgCalories: number | null;
  target: number | null;
  /** יעד פחות ממוצע: חיובי = גירעון, שלילי = עודף (קק"ל/יום) */
  deficitPerDay: number | null;
  /** שינוי משקל בפועל בתקופה (מקו המגמה), ק"ג */
  actualWeightChangeKg: number | null;
  /** קצב בפועל, ק"ג/שבוע (חיובי = ירידה) */
  paceKgPerWeek: number | null;
  /** שינוי צפוי מהגירעון הקלורי בתקופה, ק"ג (חיובי = ירידה) */
  expectedChangeKg: number | null;
  /** טווח התאריכים בפועל (YYYY-MM-DD מקומי), כולל בשני הקצוות — לתצוגה ולדפדוף */
  fromDate: string;
  toDate: string;
  /** true אם toDate הוא היום (התקופה "הנוכחית") — לא נתון היסטורי */
  isCurrent: boolean;
}

/** סכום קלוריות יומי מ-`from` עד `to` (כולל שני הקצוות), מהישן לחדש */
async function fetchDayCalories(from: Date, to: Date): Promise<DayCalories[]> {
  const fromTs = localDayStart(from);
  const toTs = localDayStart(dayBackFrom(to, -1));
  const userId = await currentUserId();
  const { data, error } = await supabase
    .from('litebite_entries')
    .select('eaten_at, calories')
    .eq('user_id', userId)
    .gte('eaten_at', fromTs)
    .lt('eaten_at', toTs);
  if (error) throw error;
  const byDay = new Map<string, number>();
  for (const r of (data ?? []) as { eaten_at: string; calories: number | null }[]) {
    const key = localDateStr(new Date(r.eaten_at));
    byDay.set(key, (byDay.get(key) ?? 0) + (r.calories ?? 0));
  }
  const spanDays = Math.round((to.getTime() - from.getTime()) / 86400000) + 1;
  const out: DayCalories[] = [];
  for (let i = 0; i < spanDays; i++) {
    const key = localDateStr(dayBackFrom(to, spanDays - 1 - i));
    const sum = byDay.get(key);
    out.push({ date: key, calories: sum ?? 0, logged: sum !== undefined });
  }
  return out;
}

/**
 * offsetPeriods: כמה תקופות אחורה מהתקופה הנוכחית. 0 = הנוכחית (מסתיימת היום).
 * ל-days=30: גלגול קבוע של 30 יום, כמו קודם. ל-days=7: מיושר ללוח שנה (ראשון-שבת) —
 * offset=0 היא "השבוע הזה עד היום" (חלקי אם עוד לא הגענו לשבת), ומ-offset=1 ואילך
 * שבוע לוח מלא ראשון-שבת — כדי לאפשר "השבוע שעבר" מדויק לתכנון השבוע הבא.
 */
export async function fetchPeriodReport(days: number, target: number | null, offsetPeriods = 0): Promise<PeriodReport> {
  const today = localDayBack(0);
  let from: Date, to: Date;
  if (days === 7) {
    const weekStart = dayBackFrom(today, today.getDay()); // ראשון של השבוע הנוכחי
    if (offsetPeriods === 0) {
      from = weekStart;
      to = today;
    } else {
      from = dayBackFrom(weekStart, offsetPeriods * 7);
      to = dayBackFrom(weekStart, offsetPeriods * 7 - 6);
    }
  } else {
    to = localDayBack(offsetPeriods * days);
    from = dayBackFrom(to, days - 1);
  }
  const spanDays = Math.round((to.getTime() - from.getTime()) / 86400000) + 1;
  const [cals, weights] = await Promise.all([
    fetchDayCalories(from, to),
    fetchWeightsUntil(to, spanDays + 7),
  ]);
  const logged = cals.filter((d) => d.logged);
  const avgCalories = logged.length ? logged.reduce((s, d) => s + d.calories, 0) / logged.length : null;
  const deficitPerDay = target != null && avgCalories != null ? target - avgCalories : null;

  // שינוי משקל בפועל: קו המגמה בתחילת התקופה מול סופה
  const trend = movingAverage(weights);
  const periodFrom = localDateStr(from);
  const inPeriod = trend.filter((p) => p.date >= periodFrom);
  let actualWeightChangeKg: number | null = null;
  let paceKgPerWeek: number | null = null;
  if (inPeriod.length >= 2) {
    const first = inPeriod[0], last = inPeriod[inPeriod.length - 1];
    actualWeightChangeKg = first.weightKg - last.weightKg; // חיובי = ירידה
    const spanTrendDays = Math.max(1, (new Date(last.date).getTime() - new Date(first.date).getTime()) / 86400000);
    paceKgPerWeek = (actualWeightChangeKg / spanTrendDays) * 7;
  }
  // שינוי צפוי מהגירעון: גירעון/יום × ימים / 7700
  const expectedChangeKg = deficitPerDay != null ? (deficitPerDay * spanDays) / 7700 : null;

  return {
    days: spanDays, loggedDays: logged.length, avgCalories, target, deficitPerDay, actualWeightChangeKg, paceKgPerWeek, expectedChangeKg,
    fromDate: localDateStr(from), toDate: localDateStr(to), isCurrent: offsetPeriods === 0,
  };
}
